"use client";

import { useCallback, useMemo } from "react";
import type { FieldValues, UseFormReturn } from "react-hook-form";
import type { SimpleTableConfig } from "../../UniversalCRMTable/types";
import type { FieldRenderContext } from "../form-field-renderers/context";

interface UseFormFieldContextProps {
  form: UseFormReturn<FieldValues>;
  config: SimpleTableConfig<any>;
  customComponents: Record<string, any>;
  appUserId: string | undefined;
  clientOptions: any[];
  clientsLoading: boolean;
  isSearching: boolean;
  searchValue: string;
  setSearchValue: (value: string) => void;
  isClientUser?: boolean;
  clientSelfContactId?: string;
  clientSelfContactName?: string;
  expandInlineVariant?: "replace" | "floating";
}

/**
 * Creates and memoizes field render context for form field rendering
 */
export function useFormFieldContext({
  form,
  config,
  customComponents,
  appUserId,
  clientOptions,
  clientsLoading,
  isSearching,
  searchValue,
  setSearchValue,
  isClientUser,
  clientSelfContactId,
  clientSelfContactName,
  expandInlineVariant = "replace",
}: UseFormFieldContextProps) {
  const {
    register,
    setValue,
    watch,
    formState: { errors },
  } = form;

  // Watch all form values to avoid conditional hook calls in renderField
  const formData = watch();
  const watchedValues = useMemo(() => {
    return formData;
  }, [formData]);

  // Create field render context
  const createFieldContext = useCallback(
    (fieldName: string): FieldRenderContext => ({
      fieldName,
      field: config.fields[fieldName],
      register,
      setValue,
      watchedValues,
      errors,
      config,
      customComponents,
      appUserId,
      clientOptions,
      clientsLoading,
      isSearching,
      searchValue,
      setSearchValue,
      isClientUser,
      clientSelfContactId,
      clientSelfContactName,
      expandInlineVariant,
    }),
    [
      config,
      register,
      setValue,
      watchedValues,
      errors,
      customComponents,
      appUserId,
      clientOptions,
      clientsLoading,
      isSearching,
      searchValue,
      setSearchValue,
      isClientUser,
      clientSelfContactId,
      clientSelfContactName,
      expandInlineVariant,
    ]
  );

  return {
    createFieldContext,
    watchedValues,
  };
}
